import { supabase } from "@/lib/supabase";
import { recordActivity } from "@/services/activityLog";

/**
 * Zone tooling for bulk lead assignment (Phase 2B).
 *
 * Wraps the SECURITY DEFINER RPCs added in migration 0012 plus the existing
 * `leads_in_zone` RPC from 0001. All writes are admin-only; that's enforced
 * server-side, but the UI also hides the controls for non-admin callers.
 */

export type ZoneShape = "polygon" | "circle" | "zip_set";

export interface ZoneRow {
  id: string;
  team_id: string;
  name: string;
  description: string | null;
  shape: ZoneShape;
  /** For circle zones: the original center/radius (the table also stores
   *  the buffered polygon in `geom`, but we don't read it client-side). */
  center_lat: number | null;
  center_lng: number | null;
  radius_m: number | null;
  zip_codes: string[] | null;
  color: string;
  default_assignee: string | null;
  created_by: string;
  created_at: string;
  /** GeoJSON-serialized polygon ring for polygon + circle zones (null for
   *  zip-set zones, which have no spatial component). The frontend parses
   *  this and converts [lng, lat] → [lat, lng] for Leaflet. */
  geom_geojson: string | null;
}

export async function listZones(): Promise<ZoneRow[]> {
  // Calls the migration-0016 RPC so polygon zones come back with their
  // geometry attached (as GeoJSON). Circle/zip-set zones come through too;
  // geom_geojson is null for zip-sets which have no spatial component.
  const { data, error } = await supabase.rpc("list_zones_with_geometry");
  if (error) throw error;
  return ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
    id: r.id as string,
    team_id: r.team_id as string,
    name: r.name as string,
    description: (r.description as string | null) ?? null,
    shape: r.shape as ZoneRow["shape"],
    center_lat: (r.center_lat as number | null) ?? null,
    center_lng: (r.center_lng as number | null) ?? null,
    radius_m: (r.radius_m as number | null) ?? null,
    zip_codes: (r.zip_codes as string[] | null) ?? null,
    color: r.color as string,
    default_assignee: (r.default_assignee as string | null) ?? null,
    created_by: r.created_by as string,
    created_at: r.created_at as string,
    geom_geojson: (r.geom_geojson as string | null) ?? null,
  }));
}

export async function createZipSetZone(input: {
  name: string;
  zip_codes: string[];
  color?: string;
  description?: string;
}): Promise<string> {
  const { data, error } = await supabase.rpc("create_zone_zip_set", {
    p_name: input.name,
    p_zip_codes: input.zip_codes,
    p_color: input.color ?? "#0ea5e9",
    p_description: input.description ?? null,
  });
  if (error) throw error;
  const zoneId = data as string;
  void recordActivity({
    kind: "zone_create",
    target_type: "zone",
    target_id: zoneId,
    summary: `Created ZIP zone "${input.name}" (${input.zip_codes.length} ZIPs)`,
  });
  return zoneId;
}

export async function createPolygonZone(input: {
  name: string;
  /** Array of [lat, lng] pairs forming the polygon's outer ring. */
  points: Array<[number, number]>;
  color?: string;
  description?: string;
}): Promise<string> {
  const { data, error } = await supabase.rpc("create_zone_polygon", {
    p_name: input.name,
    p_points: input.points,
    p_color: input.color ?? "#0ea5e9",
    p_description: input.description ?? null,
  });
  if (error) throw error;
  const zoneId = data as string;
  void recordActivity({
    kind: "zone_create",
    target_type: "zone",
    target_id: zoneId,
    summary: `Created polygon zone "${input.name}"`,
  });
  return zoneId;
}

export async function createCircleZone(input: {
  name: string;
  center_lat: number;
  center_lng: number;
  radius_m: number;
  color?: string;
  description?: string;
}): Promise<string> {
  const { data, error } = await supabase.rpc("create_zone_circle", {
    p_name: input.name,
    p_center_lat: input.center_lat,
    p_center_lng: input.center_lng,
    p_radius_m: input.radius_m,
    p_color: input.color ?? "#0ea5e9",
    p_description: input.description ?? null,
  });
  if (error) throw error;
  const zoneId = data as string;
  void recordActivity({
    kind: "zone_create",
    target_type: "zone",
    target_id: zoneId,
    summary: `Created circle zone "${input.name}" (${Math.round(input.radius_m)} m)`,
  });
  return zoneId;
}

export async function deleteZone(id: string): Promise<void> {
  const { error } = await supabase.from("zones").delete().eq("id", id);
  if (error) throw error;
  void recordActivity({
    kind: "zone_delete",
    target_type: "zone",
    target_id: id,
    summary: "Deleted zone",
  });
}

export interface LeadInZone {
  id: string;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  address_street: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip: string | null;
  phone: string | null;
  status: string;
  lat: number | null;
  lng: number | null;
  assigned_to: string | null;
}

/**
 * Returns every lead matching the zone (PostGIS for polygon/circle, ZIP match
 * for zip_set). RPC is SECURITY INVOKER + RLS-scoped — non-team-members
 * never see another team's leads.
 */
export async function listLeadsInZone(zoneId: string): Promise<LeadInZone[]> {
  const { data, error } = await supabase.rpc("leads_in_zone", { p_zone_id: zoneId });
  if (error) throw error;
  return (data ?? []) as unknown as LeadInZone[];
}

/**
 * Bulk-assigns every lead matching the zone to `assignee` (or clears the
 * field when assignee is null). Returns the count of rows updated.
 */
export async function assignZoneLeads(zoneId: string, assignee: string | null): Promise<number> {
  const { data, error } = await supabase.rpc("assign_zone_leads", {
    p_zone_id: zoneId,
    p_assignee: assignee,
  });
  if (error) throw error;
  const affected = (data ?? 0) as number;
  void recordActivity({
    kind: "zone_assign",
    target_type: "zone",
    target_id: zoneId,
    summary: assignee
      ? `Assigned ${affected} zone lead${affected === 1 ? "" : "s"}`
      : `Unassigned ${affected} zone lead${affected === 1 ? "" : "s"}`,
    metadata: { assignee, affected },
  });
  return affected;
}
